import React, { useState, useEffect } from 'react'
import FyMonthlyBarChart from '../../components/Charts/FyMonthlyBarChart'
import { currentFyLabel } from '../../utils/financialYearMonths'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useModuleView } from '../../hooks/useModuleView'
import ModuleOverviewCard from '../../components/Module/ModuleOverviewCard'
import ModuleViewBackLink from '../../components/Module/ModuleViewBackLink'
import procurementDashboardService from '../../services/procurementDashboardService'
import { formatCurrency } from '../../utils/currencyFormat'
import { 
  Users,
  Activity,
  Clock,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Brain,
  DollarSign,
  Target,
  AlertTriangle,
  CheckCircle,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  MessageSquare,
  FileText,
  Briefcase,
  ShoppingCart,
  Package,
  Truck,
  ClipboardList,
  UserCheck,
  Wrench,
  HelpCircle
} from 'lucide-react'
// Consolidated CSS is imported globally'

const Procurement = () => {
  const navigate = useNavigate()
  const moduleView = useModuleView()
  const [showHelp, setShowHelp] = useState(false)
  const [helpTab, setHelpTab] = useState('masters')
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)

  // Load dashboard data for overview metrics and masters/transactions counts
  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const response = await procurementDashboardService.getDashboardStats()
      if (response.success) {
        setDashboardData(response.data)
      } else {
        if (!response._errorHandled) {
          toast.error(response.message || 'Failed to load dashboard data')
        }
      }
    } catch (error) {
      console.error('Error loading procurement dashboard data:', error)
      if (!error._errorHandled) {
        if (error.response) {
          toast.error(error.response.data?.message || 'Failed to load dashboard data')
        } else if (!error.request) {
          toast.error('Failed to load dashboard data')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatCurrencyDisplay = (amount, currency = 'INR') => formatCurrency(amount, currency, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Just now'
    const date = new Date(timestamp)
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)
    
    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`
    return date.toLocaleDateString()
  }

  const mastersHelpContent = (
    <>
      <h3>Procurement Masters – Overview</h3>
      <p>Masters define the foundational data used across purchase requests, purchase orders, receipts, and vendor invoices. Configure these before creating transactions.</p>

      <h4>Suppliers</h4>
      <p><strong>Purpose:</strong> Maintain vendor profiles and supplier information for sourcing and ordering.</p>
      <p><strong>Details:</strong> Use the Suppliers card to open the supplier master list. Add, edit, or view suppliers with details such as name, contact person, address, payment terms, and bank information. Suppliers are linked to purchase orders and invoices. Set up suppliers before creating POs or recording vendor invoices.</p>

      <h4>Products</h4>
      <p><strong>Purpose:</strong> Maintain the product catalog and item specifications used in procurement.</p>
      <p><strong>Details:</strong> The Products master holds item codes, names, descriptions, unit of measure, and optional specifications. Products are selected in purchase requisitions and purchase orders. Use the list to create or edit product records and keep the catalog aligned with what you buy.</p>

      <h4>Services</h4>
      <p><strong>Purpose:</strong> Maintain the service catalog for procuring services (e.g. consulting, maintenance, subscriptions).</p>
      <p><strong>Details:</strong> The Services master lists services that can be requested or ordered alongside or instead of products. Each service typically has a code, name, description, and unit. Use it for non-inventory procurement so purchase requests and POs can reference both products and services.</p>

      <h4>Categories</h4>
      <p><strong>Purpose:</strong> Define purchase categories and classifications for reporting and approval routing.</p>
      <p><strong>Details:</strong> Categories (e.g. IT Equipment, Office Supplies, Services) group purchases for reporting and policy. Create and manage category codes and names in the Categories master. Categories appear in requisitions, POs, and reports; they can also drive approval limits when used with Approvers.</p>

      <h4>Approvers</h4>
      <p><strong>Purpose:</strong> Set up the purchase approval hierarchy and approval limits (who can approve up to what amount or category).</p>
      <p><strong>Details:</strong> The Approvers master defines approver roles, users, and their approval limits. Purchase requests and POs are routed based on this setup. Configure levels (e.g. L1, L2) and limits so that requests are sent to the right approvers automatically.</p>

      <h3>How to use</h3>
      <p>Click any card to open that master’s list screen. Use <strong>New</strong> or <strong>Create</strong> (or equivalent) to add records. Edit or view existing rows from the list. Ensure Suppliers, Products or Services, and Categories are set up before creating purchase requests or orders; configure Approvers so purchase approvals route correctly.</p>
    </>
  )

  const transactionsHelpContent = (
    <>
      <h3>Procurement Transactions – Overview</h3>
      <p>Transactions cover the full procurement flow: request → order → receipt → invoice. Use these after masters (Suppliers, Products/Services, Categories, Approvers) are configured.</p>

      <h4>Purchase Requests</h4>
      <p><strong>Purpose:</strong> Employees raise purchase requests (requisitions) for goods or services before a formal order is placed.</p>
      <p><strong>Details:</strong> Open Purchase Requests to view and create requisitions. Each request typically includes requester, department, items (products or services), quantities, estimated cost, justification, and optional delivery date. Requests are submitted for approval based on the Approvers master. Once approved, they can be converted to Purchase Orders. Use the list to filter by status (Draft, Submitted, Approved, Rejected, Converted to PO).</p>

      <h4>Purchase Orders</h4>
      <p><strong>Purpose:</strong> Create and send formal purchase orders to suppliers for approved requests or direct procurement.</p>
      <p><strong>Details:</strong> The Purchase Orders screen lists and creates POs. Link a PO to a supplier and add line items (products or services), quantities, rates, and terms. POs can be generated from approved purchase requests or created from scratch. Track status (Draft, Sent, Partially Received, Closed). Send the PO to the supplier and use it later for Receipts and Invoices.</p>

      <h4>Receipts</h4>
      <p><strong>Purpose:</strong> Record goods received and delivery confirmations against purchase orders.</p>
      <p><strong>Details:</strong> Use Receipts (Goods Receipt Note / GRN) to record what was received, when, and in what quantity. Match receipts to PO lines to update fulfilment and stock (if applicable). Partial receipts are supported. Receipt data supports three-way matching (PO → Receipt → Invoice) and delivery performance reporting.</p>

      <h4>Invoices</h4>
      <p><strong>Purpose:</strong> Record supplier invoices and track payment processing for procurement.</p>
      <p><strong>Details:</strong> The Invoices (Vendor Invoice) screen is where you capture supplier invoices. Link each invoice to a PO and optionally to receipts. Enter invoice number, date, line-level or total amount, tax, and payment terms. Track status (Pending, Approved, Paid). Use it for accruals, payment scheduling, and cost tracking.</p>

      <h3>How to use</h3>
      <p>Click any card to open that transaction’s list or create screen. <strong>Purchase Requests</strong>: create a requisition, add items, submit for approval; after approval, convert to PO. <strong>Purchase Orders</strong>: create or generate from request, add supplier and lines, send to supplier. <strong>Receipts</strong>: create a goods receipt against a PO and record received quantities. <strong>Invoices</strong>: record vendor invoices against POs and receipts, then process payment as per your process.</p>
    </>
  )

  const getHelpTitle = () => helpTab === 'transactions' ? 'Procurement Transactions – Help' : 'Procurement Masters – Help'
  const getHelpContent = () => helpTab === 'transactions' ? transactionsHelpContent : mastersHelpContent

  return (
    <div className="sales-dashboard">
      {/* Page Header */}
      <div className="page-header" style={{textAlign: 'left'}}>
        <div className="page-title" style={{textAlign: 'left', alignItems: 'flex-start'}}>
          <h1 style={{textAlign: 'left', margin: '0 0 4px 0'}}>Procurement</h1>
          <p style={{textAlign: 'left', margin: 0}}>Manage purchasing, suppliers, and procurement processes</p>
        </div>
      </div>

      {/* Tab Content */}
      {moduleView === 'overview' && (
        <div className="tab-content">
          <ModuleViewBackLink onClick={() => navigate('/procurement')} />

          {/* Key Metrics Grid */}
          <div className="dashboard-metrics-grid">
            {/* Procurement Metrics */}
            <div className="metric-card revenue">
              <div className="metric-header">
                <div className="metric-icon">
                  <ShoppingCart size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.purchaseOrders?.changeType || 'positive'}`}>
                  <TrendingUp size={12} />
                  <span>{dashboardData?.purchaseOrders?.change >= 0 ? '+' : ''}{dashboardData?.purchaseOrders?.change || 0}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : (dashboardData?.purchaseOrders?.count || 0)}
              </div>
              <div className="metric-title">Purchase Orders</div>
              <div className="metric-subtitle">This month</div>
            </div>

            <div className="metric-card customers">
              <div className="metric-header">
                <div className="metric-icon">
                  <DollarSign size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.purchaseValue?.changeType || 'positive'}`}>
                  <TrendingUp size={12} />
                  <span>{dashboardData?.purchaseValue?.change >= 0 ? '+' : ''}{dashboardData?.purchaseValue?.change?.toFixed(1) || 0}%</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : formatCurrencyDisplay(dashboardData?.purchaseValue?.value || 0, dashboardData?.purchaseValue?.currency || 'INR')}
              </div>
              <div className="metric-title">Purchase Value</div>
              <div className="metric-subtitle">This month</div>
            </div>

            <div className="metric-card churn">
              <div className="metric-header">
                <div className="metric-icon">
                  <AlertTriangle size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.pendingApproval?.count > 0 ? 'negative' : 'positive'}`}>
                  <TrendingDown size={12} />
                  <span>{dashboardData?.pendingApproval?.status || 'CLEAR'}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : (dashboardData?.pendingApproval?.count || 0)}
              </div>
              <div className="metric-title">Pending Approval</div>
              <div className="metric-subtitle">Purchase requests</div>
            </div>

            <div className="metric-card burn">
              <div className="metric-header">
                <div className="metric-icon">
                  <Truck size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.deliveryRate?.status === 'ON_TIME' ? 'positive' : dashboardData?.deliveryRate?.status === 'NO_DATA' ? 'neutral' : 'negative'}`}>
                  <CheckCircle size={12} />
                  <span>{(dashboardData?.deliveryRate?.status || 'NO_DATA').replace(/_/g, ' ')}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading
                  ? '...'
                  : dashboardData?.deliveryRate?.status === 'NO_DATA'
                    ? '—'
                    : `${dashboardData?.deliveryRate?.rate || 0}%`}
              </div>
              <div className="metric-title">Delivery Rate</div>
              <div className="metric-subtitle">Supplier performance</div>
            </div>
          </div>

          {/* Action Items & Alerts */}
          <div className="dashboard-action-section">
            <div className="dashboard-action-items">
              <div className="section-header">
                <h3>Procurement Action Items</h3>
                <span className="item-count">{dashboardData?.actionItems?.length || 0} items</span>
              </div>
              <div className="action-list">
                {loading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading action items...</div>
                ) : dashboardData?.actionItems && dashboardData.actionItems.length > 0 ? (
                  dashboardData.actionItems.map((item, index) => {
                    const iconMap = {
                      approval: AlertTriangle,
                      supplier: Package,
                      inventory: ClipboardList,
                      delivery: Truck,
                    }
                    const Icon = iconMap[item.type] || AlertTriangle
                    return (
                      <div key={index} className={`action-item ${item.priority}`}>
                        <div className="action-icon">
                          <Icon size={16} />
                        </div>
                        <div className="action-content">
                          <div className="action-title">{item.title}</div>
                          <div className="action-description">{item.description}</div>
                        </div>
                        <div className="action-time">{formatTimeAgo(item.timestamp)}</div>
                      </div>
                    )
                  })
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No action items</div>
                )}
              </div>
            </div>

            <div className="dashboard-team-activity">
              <div className="section-header">
                <h3>Procurement Team Activity</h3>
                <span className="activity-count">{dashboardData?.teamActivity?.length || 0} updates</span>
              </div>
              <div className="activity-list">
                {loading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading team activity...</div>
                ) : dashboardData?.teamActivity && dashboardData.teamActivity.length > 0 ? (
                  dashboardData.teamActivity.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className="activity-avatar">{activity.employeeInitials || 'UN'}</div>
                      <div className="activity-content">
                        <div className="activity-text">
                          <strong>{activity.employeeName || 'Unknown'}</strong> {activity.activity}
                        </div>
                        <div className="activity-time">{formatTimeAgo(activity.timestamp)}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No recent activity</div>
                )}
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="dashboard-charts-section">
            <div className="dashboard-chart-card revenue-chart">
              <div className="chart-header">
                <h3>Purchase Value Trend</h3>
                <p>{dashboardData?.financialYear ? `FY ${dashboardData.financialYear}` : `FY ${currentFyLabel()}`}</p>
              </div>
              <div className="chart-content">
                <FyMonthlyBarChart
                  series={dashboardData?.purchaseValueTrend}
                  valueKey="revenue"
                  formatTooltip={(v) => formatCurrencyDisplay(v, 'INR')}
                  emptyMessage="No purchase orders this financial year"
                />
              </div>
            </div>

            <div className="dashboard-chart-card customer-health">
              <div className="chart-header">
                <h3>Purchase Category Distribution</h3>
                <p>Current month</p>
              </div>
              <div className="chart-content">
                <div className="health-distribution">
                  {loading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading...</div>
                  ) : dashboardData?.categoryDistribution?.length > 0 ? (
                    dashboardData.categoryDistribution.map((row, index) => {
                      const classes = ['excellent', 'good', 'at-risk', 'churned']
                      const cls = classes[index % classes.length]
                      return (
                        <div key={row.name || index} className={`health-segment ${cls}`}>
                          <div className="segment-bar" style={{ width: `${Math.max(row.percent || 0, row.amount > 0 ? 4 : 0)}%` }} />
                          <div className="segment-info">
                            <span className="segment-label">{row.name}</span>
                            <span className="segment-count">{formatCurrencyDisplay(row.amount || 0, 'INR')}</span>
                          </div>
                        </div>
                      )
                    })
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                      No purchases this month
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'masters' && (
        <div className="tab-content">
          <div className="procurement-masters-content">
            <div className="procurement-masters-header d-flex justify-content-between align-items-start flex-wrap gap-2" style={{ flexWrap: 'wrap' }}>
              <div>
                <h2>Procurement Masters</h2>
                <p>Manage your procurement master data and configurations</p>
              </div>
              <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => { setHelpTab('masters'); setShowHelp(true); }} title="Help">
                <HelpCircle size={18} />
                <span className="d-none d-sm-inline">Help</span>
              </button>
            </div>
            <div className="procurement-masters-grid">
              <div className="procurement-master-card clickable" onClick={() => navigate('/procurement/supplier-master')}>
                <div className="procurement-master-icon">
                  <Users size={24} />
                </div>
                <div className="procurement-master-content">
                  <h3>Suppliers</h3>
                  <p>Vendor profiles and supplier information</p>
                  <div className="procurement-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.suppliers ?? 0} Suppliers`}
                  </div>
                </div>
              </div>
              <div className="procurement-master-card clickable" onClick={() => navigate('/procurement/product-master')}>
                <div className="procurement-master-icon">
                  <Package size={24} />
                </div>
                <div className="procurement-master-content">
                  <h3>Products</h3>
                  <p>Product catalog and item specifications</p>
                  <div className="procurement-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.products ?? 0} Products`}
                  </div>
                </div>
              </div>
              <div className="procurement-master-card clickable" onClick={() => navigate('/procurement/procurement-service-master')}>
                <div className="procurement-master-icon">
                  <Wrench size={24} />
                </div>
                <div className="procurement-master-content">
                  <h3>Services</h3>
                  <p>Service catalog for procurement</p>
                  <div className="procurement-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.services ?? 0} Services`}
                  </div>
                </div>
              </div>
              <div className="procurement-master-card clickable" onClick={() => navigate('/procurement/category-master')}>
                <div className="procurement-master-icon">
                  <Target size={24} />
                </div>
                <div className="procurement-master-content">
                  <h3>Categories</h3>
                  <p>Purchase categories and classifications</p>
                  <div className="procurement-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.categories ?? 0} Categories`}
                  </div>
                </div>
              </div>
              <div className="procurement-master-card clickable" onClick={() => navigate('/procurement/procurement-approver-master')}>
                <div className="procurement-master-icon">
                  <UserCheck size={24} />
                </div>
                <div className="procurement-master-content">
                  <h3>Approvers</h3>
                  <p>Purchase approval hierarchy and limits</p>
                  <div className="procurement-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.approvers ?? 0} Approvers`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title={getHelpTitle()}>
        {getHelpContent()}
      </HelpModal>

      {moduleView === 'transactions' && (
        <div className="tab-content">
          <div className="procurement-transactions-content">
            <div className="procurement-transactions-header d-flex justify-content-between align-items-start flex-wrap gap-2">
              <div>
                <h2>Procurement Transactions</h2>
                <p>Track and manage all procurement activities and orders</p>
              </div>
              <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => { setHelpTab('transactions'); setShowHelp(true); }} title="Help">
                <HelpCircle size={18} />
                <span className="d-none d-sm-inline">Help</span>
              </button>
            </div>
            <div className="procurement-transactions-grid">
              <ModuleOverviewCard
                prefix="procurement-transaction"
                moduleName="Procurement"
                showArrow={false}
                onClick={() => navigate('/procurement?view=overview')}
              />
              <div className="procurement-transaction-card clickable" onClick={() => navigate('/procurement/purchase-requisition')}>
                <div className="procurement-transaction-icon">
                  <ClipboardList size={24} />
                </div>
                <div className="procurement-transaction-content">
                  <h3>Purchase Requests</h3>
                  <p>Employee purchase requests and requisitions</p>
                  <div className="procurement-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.requisitions ?? 0} Requests`}
                  </div>
                </div>
              </div>
              <div className="procurement-transaction-card clickable" onClick={() => navigate('/procurement/purchase-order')}>
                <div className="procurement-transaction-icon">
                  <ShoppingCart size={24} />
                </div>
                <div className="procurement-transaction-content">
                  <h3>Purchase Orders</h3>
                  <p>Generated and sent purchase orders</p>
                  <div className="procurement-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.purchaseOrders ?? 0} Orders`}
                  </div>
                </div>
              </div>
              <div className="procurement-transaction-card clickable" onClick={() => navigate('/procurement/goods-receipt-note')}>
                <div className="procurement-transaction-icon">
                  <Truck size={24} />
                </div>
                <div className="procurement-transaction-content">
                  <h3>Receipts</h3>
                  <p>Goods received and delivery confirmations</p>
                  <div className="procurement-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.receipts ?? 0} Received`}
                  </div>
                </div>
              </div>
              <div className="procurement-transaction-card clickable" onClick={() => navigate('/procurement/vendor-invoice')}>
                <div className="procurement-transaction-icon">
                  <DollarSign size={24} />
                </div>
                <div className="procurement-transaction-content">
                  <h3>Invoices</h3>
                  <p>Supplier invoices and payment processing</p>
                  <div className="procurement-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.invoices ?? 0} Invoices`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'reports' && (
        <div className="tab-content">
          <div className="procurement-reports-content">
            <div className="procurement-reports-header">
              <h2>Procurement Reports</h2>
              <p>Comprehensive procurement analytics and reporting</p>
            </div>
            <div className="procurement-reports-grid">
              <div className="procurement-report-card clickable" onClick={() => navigate('/procurement/purchase-analysis')}>
                <div className="procurement-report-icon">
                  <BarChart3 size={24} />
                </div>
                <div className="procurement-report-content">
                  <h3>Purchase Analysis</h3>
                  <p>Purchase trends and category analysis</p>
                  <div className="procurement-report-status">Updated Today</div>
                </div>
              </div>
              <div className="procurement-report-card clickable" onClick={() => navigate('/procurement/supplier-reports')}>
                <div className="procurement-report-icon">
                  <Users size={24} />
                </div>
                <div className="procurement-report-content">
                  <h3>Supplier Reports</h3>
                  <p>Supplier performance and evaluation</p>
                  <div className="procurement-report-status">Updated Today</div>
                </div>
              </div>
              <div className="procurement-report-card clickable" onClick={() => navigate('/procurement/inventory-reports')}>
                <div className="procurement-report-icon">
                  <Package size={24} />
                </div>
                <div className="procurement-report-content">
                  <h3>Inventory Reports</h3>
                  <p>Inventory levels and stock analysis</p>
                  <div className="procurement-report-status">Updated Today</div>
                </div>
              </div>
              <div className="procurement-report-card clickable" onClick={() => navigate('/procurement/cost-reports')}>
                <div className="procurement-report-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="procurement-report-content">
                  <h3>Cost Reports</h3>
                  <p>Cost analysis and budget tracking</p>
                  <div className="procurement-report-status">Updated Today</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Procurement




